import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { DateTime } from "luxon";
import { Calendar } from "primereact/calendar";
import { fetchGet } from "Services/api";
import { getUsersCountByMonthData } from "Components/Stats/Data/Utilisateurs/Inscriptions";
import NivoBar from "Components/Stats/Charts/NivoBar";

/**
 * Composant graphique des utilisateurs par mois
 *
 * Une période peut être choisie pour afficher les données (défaut: 6 derniers mois)
 *
 * {@link getUsersCountByMonthData} : Fonction qui formate les données pour le graphique du nombre d'utilisateurs inscrits par mois.
 * {@link NivoBar} : Composant générique de graphique en barres implémenté avec la librairie Nivo.
 *
 * @param {Object} props - Props Redux + props du composant
 * @returns {ReactElement} Graphique du nombre d'utilisateurs inscrits par mois
 */
const UsersCountByMonth = (props) => {
  const [usersCountByMonthPeriod, setUsersCountByMonthPeriod] = useState([
    DateTime.local()
      .startOf("month")
      .minus({ months: 6 })
      .plus({ days: 1 })
      .toJSDate(),
    DateTime.local().endOf("month").minus({ months: 1 }).toJSDate(),
  ]);

  const [chartData, setChartData] = useState([]);

  /**
   * Mise à jour des données pour les utilisateurs par mois (inscrits sur une période)
   */
  async function updateUsersCountByMonth() {
    let requestData = await fetchGet(
      "/users/stats/count/register_total",
      props.auth.token,
      {
        // Filtre période, posté après la date de début et avant la date de fin
        "datePeriod[after]": usersCountByMonthPeriod[0]
          .toISOString()
          .slice(0, 10),
        "datePeriod[before]": usersCountByMonthPeriod[1]
          .toISOString()
          .slice(0, 10),
      }
    );

    setChartData(getUsersCountByMonthData(requestData));
  }

  useEffect(async () => {
    //? Si la période est définie, et correcte
    if (
      usersCountByMonthPeriod[0] instanceof Date &&
      !isNaN(usersCountByMonthPeriod[0]) &&
      usersCountByMonthPeriod[1] instanceof Date &&
      !isNaN(usersCountByMonthPeriod[1])
    )
      await updateUsersCountByMonth();
  }, [usersCountByMonthPeriod]);

  return (
    <React.Fragment>
      <div className="calendar">
        <label>Période choisie</label>
        <Calendar
          value={usersCountByMonthPeriod}
          onChange={(e) => {
            if (e.value[0] && e.value[1]) {
              let beforeDate = DateTime.fromJSDate(e.value[0]);
              let afterDate = DateTime.fromJSDate(e.value[1]).plus({
                months: 1,
              });
              setUsersCountByMonthPeriod([
                beforeDate.toJSDate(),
                afterDate.toJSDate(),
              ]);
            } else {
              setUsersCountByMonthPeriod(e.value);
            }
          }}
          showIcon
          view="month"
          dateFormat="mm/yy"
          selectionMode="range"
          maxDate={DateTime.local().minus({ months: 1 }).toJSDate()}
        />
      </div>
      <NivoBar
        colors="pastel2"
        data={chartData}
        legendYAxis="Nombre d'utilisateurs"
        keysNames={["Utilisateurs"]}
        indexName="Date"
        title={
          "Nombre d'utilisateurs inscrits par mois dans l'université " +
          props.universityName
        }
      />
    </React.Fragment>
  );
};

UsersCountByMonth.propTypes = {
  universityName: PropTypes.string,
  auth: PropTypes.object.isRequired,
};

const mapStateToProps = (state) => ({
  auth: state.auth,
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(UsersCountByMonth);
